/**
 * Раздел «Обучение» портала — этап P4 (§2, §4, §6 ТЗ портала; D185, D186).
 *
 * Экраны — **импортом из Mini App обучения** (`@study`), а не копией:
 * справочник, курсы и тесты, наставник, редактор и отчёты — те же файлы,
 * что на `study.`. Портал заменяет только навигацию Mini App (нижнюю
 * панель, строку названия и шестерёнку редактора) вкладками и адресом:
 *
 * - «Справочник» — `/study/catalog`, карточка блюда `/study/catalog/42`;
 * - «Курсы» — `/study/courses`, курс `/study/courses/7`;
 * - «Наставник» — **один раздел вместо «Помощника» и «Наставника»**
 *   (D186): разговор первым (вопрос по регламенту со ссылкой на источник),
 *   затем «Показатели», «Цели», «Микроурок», «Тренировка». У кого есть
 *   только `assistant` — только разговор; только `mentor` — только развитие;
 * - «Редактор» — только с правом «править»: блюда, курсы, регламенты,
 *   тренировки, микроуроки;
 * - «Отчёты» — S1–S8 по правилам Mini App.
 *
 * Какие вкладки видны — `routes.ts`. Это оформление: `study-api` проверяет
 * право на каждом запросе.
 */

import { Suspense, lazy, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Denied, Empty, Loading } from '@shell/states';
import type { Navigation } from '../../api';
import { Link, navigate, useRoute } from '../../router';
import { installMockStudyApi, studyApi } from './api';
import {
  editorTabs,
  mentorTabs,
  pick,
  readStudy,
  studyAccess,
  studyPath,
  tabsFor,
} from './routes';
import type { EditorTab, MentorTab, Tab, TabCode } from './routes';
import './study.css';

installMockStudyApi();

const Screens = lazy(() => import('./Screens'));

function TabRow<C extends string>({
  tabs,
  current,
  href,
  label,
  small = false,
}: {
  tabs: Tab<C>[];
  current: C;
  href: (code: C) => string;
  label: string;
  small?: boolean;
}) {
  return (
    <nav className={`study-tabs${small ? ' study-tabs--sub' : ''}`} aria-label={label}>
      {tabs.map((tab) => (
        <Link
          key={tab.code}
          to={href(tab.code)}
          className={`chip${tab.code === current ? ' chip--active' : ''}`}
          aria-current={tab.code === current ? 'page' : undefined}
        >
          {tab.title}
        </Link>
      ))}
    </nav>
  );
}

export default function Study({ nav }: { nav: Navigation }) {
  const route = useRoute();
  const access = studyAccess(nav);
  // «Открыл обучение» — событие отчёта «Активность» (S3), как при запуске
  // Mini App; заодно — объяснение сервера, когда разделов нет.
  const me = useQuery({
    queryKey: ['study', 'me'],
    queryFn: studyApi.me,
    enabled: access !== null,
    staleTime: Infinity,
    retry: false,
  });

  const tabs = access ? tabsFor(access) : [];
  const address = readStudy(route.path);
  const tab = pick(tabs, address.tab) as TabCode | null;
  const subTabs: Tab<MentorTab | EditorTab>[] =
    !access ? [] : tab === 'mentor' ? mentorTabs(access) : tab === 'editor' ? editorTabs(access) : [];
  const sub = subTabs.length > 0 ? pick(subTabs, address.sub) : null;
  const id = tab === 'catalog' || tab === 'courses' ? address.id : null;
  const canonical = tab ? (sub ? studyPath(tab, sub) : studyPath(tab, id)) : null;

  // Адрес без вкладки или с недоступной — заменить тем, что человек видит:
  // тогда ссылка, скопированная из строки браузера, ведёт туда же.
  useEffect(() => {
    if (canonical && route.path !== canonical) navigate(canonical, { replace: true });
  }, [canonical, route.path]);

  if (!access) return <Denied />;

  const header = (
    <header className="page__head study-page__head">
      <p className="page__eyebrow">Люди</p>
      <h1 className="page__title">Обучение</h1>
    </header>
  );

  if (!tab) {
    return (
      <div className="page study-page">
        {header}
        {me.isLoading ? (
          <Loading />
        ) : (
          <Empty
            title="Пока показывать нечего"
            text={
              me.data?.problem ??
              'Разделы обучения открываются по должности. Если они нужны вам по работе — обратитесь к управляющему.'
            }
          />
        )}
      </div>
    );
  }

  return (
    <div className="page study-page">
      {header}
      <TabRow
        tabs={tabs}
        current={tab}
        href={(code) => studyPath(code)}
        label="Разделы обучения"
      />
      {sub && subTabs.length > 1 && (
        <TabRow
          tabs={subTabs}
          current={sub}
          href={(code) => studyPath(tab, code)}
          label={tab === 'mentor' ? 'Наставник' : 'Редактор'}
          small
        />
      )}
      <div className="study-scope">
        <Suspense fallback={<Loading />}>
          <Screens state={{ tab, sub, id }} access={access} />
        </Suspense>
      </div>
    </div>
  );
}
